/**
 * 依存ゼロの最小 pub/sub ストア。
 *
 * 設計書セクション 6.2 の実装。React / Zustand / Redux 等の外部依存を
 * 持ち込まず、Vanilla TypeScript のみで単一真実を管理する。
 *
 * 使い方:
 * ```ts
 * import { createStore } from './store';
 * import { DEFAULT_STATE } from './defaults';
 *
 * const store = createStore(DEFAULT_STATE);
 * const unsubscribe = store.subscribe((state, prev) => {
 *   if (state.aspectRatio !== prev.aspectRatio) resizeCanvas(state);
 * });
 * store.set({ aspectRatio: '16:9' }); // listener が発火
 * unsubscribe();                      // 解除
 * ```
 */

/**
 * ストアの購読者シグネチャ。`set()` のたびに新旧両方の state を受け取る。
 */
export type Listener<T> = (state: T, prev: T) => void;

/**
 * `createStore` の戻り値。読み取り / 書き込み（shallow merge）/ 購読の
 * 3 つの操作のみを公開する。
 */
export interface Store<T> {
  /** 現在の state を取得する（常に immutable な参照を返す前提）。 */
  get: () => T;
  /** `patch` を既存 state に shallow merge し、全 listener を通知する。 */
  set: (patch: Partial<T>) => void;
  /**
   * listener を登録し、解除関数を返す。
   * 解除関数は冪等で、複数回呼ばれても安全。
   */
  subscribe: (listener: Listener<T>) => () => void;
}

/**
 * 最小 pub/sub ストアを生成する。
 *
 * 実装メモ:
 * - listener 集合には `Set` を用いる。`add` / `delete` がともに O(1) で、
 *   購読解除の冪等性（同一 listener を複数回登録しても 1 回分として扱う）
 *   を素直に表現できるため。
 * - `set()` では listener を走査する前に `Array.from(listeners)` で
 *   スナップショットを取る。listener の中で `subscribe` / `unsubscribe`
 *   または `set` が呼ばれた場合でも、当該 tick で呼び出される listener
 *   集合が途中で変化しないことを保証するため（`Set.forEach` は走査中の
 *   変更を部分的に反映してしまう）。
 * - `set()` は shallow merge。ネストしたオブジェクト（`resolution` など）は
 *   呼び出し側で新しいタプルを作って渡すこと。
 *
 * @param initial - ストアの初期 state
 * @returns `get` / `set` / `subscribe` を公開する `Store<T>`
 */
export function createStore<T extends object>(initial: T): Store<T> {
  let state: T = initial;
  const listeners = new Set<Listener<T>>();

  return {
    get: (): T => state,

    set: (patch: Partial<T>): void => {
      const prev = state;
      state = { ...state, ...patch };
      // listener 内での subscribe / unsubscribe / set による
      // listeners 集合の変化に耐えるよう、走査前にスナップショットを取る。
      for (const listener of Array.from(listeners)) {
        listener(state, prev);
      }
    },

    subscribe: (listener: Listener<T>): (() => void) => {
      listeners.add(listener);
      return (): void => {
        listeners.delete(listener);
      };
    },
  };
}
